import { addDays } from 'date-fns';
import { MoveLeft, MoveRight } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/Button';
import type { Period } from '@/entities/types';
import {
  getPeriodEndAt,
  getQuarterStartAt,
  getWeekStartAt,
  getYearStartAt,
  getYesterdayStartAt,
  toFullDayDateString,
} from '@/utils/datetime';

const capitalizeFirstLetter = (string: string) =>
  string.length === 0
    ? string
    : string.charAt(0).toUpperCase() + string.slice(1);

function getButtonData(targetStartAt: Date, period: Period) {
  const dateInPreviousPeriod = addDays(targetStartAt, -1);
  let previousAt: Date;
  if (period === 'day') {
    previousAt = getYesterdayStartAt(targetStartAt);
  } else if (period === 'week') {
    previousAt = getWeekStartAt(dateInPreviousPeriod);
  } else if (period === 'quarter') {
    previousAt = getQuarterStartAt(dateInPreviousPeriod);
  } else {
    previousAt = getYearStartAt(dateInPreviousPeriod);
  }
  const nextAt = getPeriodEndAt(targetStartAt, period);

  return {
    prevNavButton: {
      label: `Previous ${period}`,
      path: `/${period}/${toFullDayDateString(previousAt)}`,
      Icon: MoveLeft,
    },
    nextNavButton: {
      label: `Next ${period}`,
      path: `/${period}/${toFullDayDateString(nextAt)}`,
      Icon: MoveRight,
    },
  };
}

interface NavButtonProps {
  label: string;
  path: string;
  Icon: React.ElementType;
}

function NavButton({ label, path, Icon }: NavButtonProps) {
  return (
    <Button
      key={label}
      className="h-8 w-8"
      variant="ghost"
      aria-label={label}
      asChild
    >
      <Link to={path}>
        <Icon size={24} />
      </Link>
    </Button>
  );
}

interface PrevNextPeriodNavigationProps {
  targetStartAt: Date;
  period: Period;
}

export function PrevNextPeriodNavigation({
  targetStartAt,
  period,
}: PrevNextPeriodNavigationProps) {
  const buttonData = getButtonData(targetStartAt, period);

  return (
    <div className="flex items-center gap-1">
      <NavButton {...buttonData.prevNavButton} />
      <div className="text-[13px]">{capitalizeFirstLetter(period)}</div>
      <NavButton {...buttonData.nextNavButton} />
    </div>
  );
}
